/**
 * Invoice Service
 * API integration for invoice management (financial module)
 *
 * @version 2025.1.0
 */

import apiClient from './apiClient';

// ============================================================================
// TYPES
// ============================================================================

export interface ExamDetailEntry {
  examName: string;
  examId: number;
  price: number;
}

export interface InvoiceItem {
  id: number;
  invoiceId: number;
  appointmentId: number | null;
  description: string;
  examDetails: ExamDetailEntry[] | null;
  unitPrice: number;
  quantity: number;
  subtotal: number;
}

export interface Invoice {
  id: number;
  type: 'SERVICE' | 'PLATFORM';
  providerId: number | null;
  companyId: number;
  referenceMonth: number;
  referenceYear: number;
  status: 'OPEN' | 'CLOSED' | 'PAID' | 'CANCELLED';
  totalAmount: number;
  closedAt: string | null;
  paidAt: string | null;
  cancelledAt: string | null;
  notes: string | null;
  dueDate: string | null;
  company?: { id: number; legalName: string; tradeName: string };
  provider?: { id: number; legalName: string; tradeName: string };
  items?: InvoiceItem[];
}

export interface InvoiceFilters {
  type?: string;
  status?: string;
  referenceMonth?: number;
  referenceYear?: number;
  companyId?: number;
  providerId?: number;
  page?: number;
  limit?: number;
  overdue?: boolean;
}

export interface InvoiceListResponse {
  data: Invoice[];
  pagination: { total: number; page: number; limit: number; totalPages: number };
}

export interface DashboardData {
  currentMonth: {
    totalInvoices: number;
    totalAmount: number;
    serviceAmount: number;
    platformAmount: number;
    pendingCount: number;
  };
  previousMonth: {
    totalInvoices: number;
    totalAmount: number;
  };
  statusBreakdown: {
    OPEN: { count: number; totalAmount: number };
    CLOSED: { count: number; totalAmount: number };
    PAID: { count: number; totalAmount: number };
    CANCELLED: { count: number; totalAmount: number };
  };
  receivable: number;
  overdue: number;
  monthlyEvolution: Array<{
    month: number;
    year: number;
    totalAmount: number;
  }>;
}

// ============================================================================
// SERVICE
// ============================================================================

class InvoiceService {
  private baseUrl = '/invoices';

  /**
   * Get paginated list of invoices
   */
  async getInvoices(filters: InvoiceFilters = {}): Promise<InvoiceListResponse> {
    const queryParams = new URLSearchParams();

    if (filters.type) queryParams.append('type', filters.type);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.referenceMonth !== undefined) queryParams.append('referenceMonth', filters.referenceMonth.toString());
    if (filters.referenceYear !== undefined) queryParams.append('referenceYear', filters.referenceYear.toString());
    if (filters.companyId !== undefined) queryParams.append('companyId', filters.companyId.toString());
    if (filters.providerId !== undefined) queryParams.append('providerId', filters.providerId.toString());
    if (filters.page !== undefined) queryParams.append('page', filters.page.toString());
    if (filters.limit !== undefined) queryParams.append('limit', filters.limit.toString());
    if (filters.overdue) queryParams.append('overdue', 'true');

    const qs = queryParams.toString();
    const url = qs ? `${this.baseUrl}?${qs}` : this.baseUrl;
    const response = await apiClient.get(url);

    // httpClient.get() already unwraps axios response.data, so `response` here
    // is the API payload: { message, data: Invoice[], pagination: {...} }
    const invoices = Array.isArray(response.data) ? response.data : response.data?.data || [];
    const pagination = response.pagination || response.data?.pagination || {
      total: 0,
      page: filters.page || 1,
      limit: filters.limit || 20,
      totalPages: 0,
    };

    return {
      data: invoices,
      pagination,
    };
  }

  /**
   * Get invoice by ID (with items)
   */
  async getInvoiceById(id: number): Promise<Invoice> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data?.data || response.data;
  }

  /**
   * Get financial dashboard data
   */
  async getDashboard(): Promise<DashboardData> {
    const response = await apiClient.get(`${this.baseUrl}/dashboard`);
    return response.data?.data || response.data;
  }

  /**
   * Close an invoice
   */
  async closeInvoice(id: number): Promise<Invoice> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/close`);
    return response.data?.data || response.data;
  }

  /**
   * Reopen an invoice
   */
  async reopenInvoice(id: number): Promise<Invoice> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/reopen`);
    return response.data?.data || response.data;
  }

  /**
   * Mark an invoice as paid
   */
  async markAsPaid(id: number): Promise<Invoice> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/pay`);
    return response.data?.data || response.data;
  }

  /**
   * Cancel an invoice
   */
  async cancelInvoice(id: number): Promise<Invoice> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/cancel`);
    return response.data?.data || response.data;
  }

  /**
   * Close all open invoices for a month
   */
  async closeMonth(month: number, year: number): Promise<{ closed: number }> {
    const response = await apiClient.post(`${this.baseUrl}/close-month`, { month, year });
    return response.data?.data || response.data;
  }

  /**
   * Generate platform invoices for a month
   */
  async generatePlatformInvoices(month: number, year: number): Promise<{
    generated: number;
    failed?: Array<{ companyId: number; companyName: string; error: string }>;
  }> {
    const response = await apiClient.post(`${this.baseUrl}/generate-platform`, { month, year });
    return response.data?.data || response.data;
  }

  /**
   * Download invoice PDF
   */
  async downloadPdf(id: number): Promise<void> {
    await apiClient.downloadFile(`${this.baseUrl}/${id}/pdf`, `fatura-${id}.pdf`);
  }

  /**
   * Export invoices as CSV with current filters
   */
  async exportCsv(filters: InvoiceFilters = {}): Promise<void> {
    const queryParams = new URLSearchParams();
    if (filters.type) queryParams.append('type', filters.type);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.referenceMonth !== undefined) queryParams.append('referenceMonth', filters.referenceMonth.toString());
    if (filters.referenceYear !== undefined) queryParams.append('referenceYear', filters.referenceYear.toString());
    if (filters.overdue) queryParams.append('overdue', 'true');

    const qs = queryParams.toString();
    const url = qs ? `${this.baseUrl}/export?${qs}` : `${this.baseUrl}/export`;
    await apiClient.downloadFile(url, 'faturas.csv');
  }
}

// Export singleton instance
export const invoiceService = new InvoiceService();
export default invoiceService;
